import type { LmsLocationFacet } from '@/interfaces/lms';

export type RelatedDestinationLink = {
    label: string;
    til: string;
    count: number;
};

const TOTAL_LINKS = 8;

export const formatCount = (count: number): string => count.toLocaleString('nb-NO');

const findNode = (facets: LmsLocationFacet[] | undefined, path: string): LmsLocationFacet | undefined => {
    for (const facet of facets ?? []) {
        if (facet.name === path) {
            return facet;
        }
        const found = findNode(facet.children, path);
        if (found) {
            return found;
        }
    }
    return undefined;
};

const availableLinks = (facets: LmsLocationFacet[]): RelatedDestinationLink[] =>
    facets
        .filter((facet) => facet.count > 0)
        .sort((a, b) => b.count - a.count)
        .map((facet) => ({ label: facet.text, til: facet.name, count: facet.count }));

const selectionLevels = (path: string, locationFacet: LmsLocationFacet[] | undefined): RelatedDestinationLink[][] => {
    const segments = path.split(';').filter(Boolean);
    const childLevels = segments
        .map((_, index) => findNode(locationFacet, segments.slice(0, index + 1).join(';'))?.children ?? [])
        .reverse();
    const allCountries = (locationFacet ?? []).flatMap((continent) => continent.children);

    return [...childLevels, allCountries].map(availableLinks);
};

export const getRelatedDestinations = (
    destinationQueries: string[],
    locationFacet: LmsLocationFacet[] | undefined,
): RelatedDestinationLink[] =>
    (destinationQueries.length === 0 ? [''] : destinationQueries)
        .flatMap((path, selection) =>
            selectionLevels(path, locationFacet).flatMap((levelLinks, level) =>
                levelLinks.map((link, rank) => ({ link, level, rank, selection })),
            ),
        )
        .sort((a, b) => a.level - b.level || a.rank - b.rank || a.selection - b.selection)
        .map(({ link }) => link)
        .filter((link) => !destinationQueries.includes(link.til))
        .filter((link, index, ordered) => ordered.findIndex((other) => other.til === link.til) === index)
        .slice(0, TOTAL_LINKS);
